import type { Column, Row } from '@innovamat/glimmer-components';
import type { TFunction } from 'i18next';

import { Stage } from '@innovamat/radiance-utils';

import type { ClassroomCapabilities } from '../../../hooks/use-classroom-capabilities';
import { ClassroomStudentsTableActionsCell } from '../components/classroom-students-table.actions-cell';
import type { ClassroomStudent } from '../types/classroom-student';

import {
  renderAvatar,
  renderDigitalPracticeSubLabel,
  renderEyDigitalPractice,
  renderHomeAccess,
  renderHomeAccessSublabel,
  renderIntervention,
  renderInterventionSubLabel,
  renderPrimaryDigitalPractice,
  renderSecondaryDigitalPractice,
  renderStudentName,
} from './render-students-table';

type Option = { value: string | number; label: string };

type Props = {
  availableLanguages: Option[];
  hasClassroomDigitalPracticeAccess: boolean;
  classroomCapabilities: ClassroomCapabilities;
  classroomHasHomeAccess: boolean;
  practiceLevelOptions: Option[];
  isRti: boolean;
  isAnonymized: boolean;
  isRosteringEnabled: boolean;
  isJourneys: boolean;
  selectedInputKey: string;
  selectedStudentId: string;
  stage: string | null;
  weeklyTimeLimitOptions: Option[];
  handleBlurInput: () => void;
  handleDeleteStudent: (student: ClassroomStudent | undefined) => void;
  handleInputChange: (key: string, value: any, selectedId?: string) => void;
  handleInviteParents: (studentId: string) => void;
  handleMoveStudent: (student: ClassroomStudent | undefined) => void;
  handleOpenAvatarEditor: (id: string, img: string | null | undefined) => void;
  handleOpenChangePasswordModal: (studentId: string) => void;
  handleOpenCompleteManuallyModal: (studentId: string) => void;
  handleOpenDigitalPracticeAccessModal: () => void;
  handleOpenDigitalAccessConfirmationModal: () => void;
  handleResendInvitation: (studentId: string) => void;
  handleResetTests: (student: ClassroomStudent | undefined) => void;
  handleSelect: (studentId: string, inputKey: string) => void;
  handleToggleHomeAccess: (value: boolean, studentId: string) => void;
  handleToggleIndividualAccess: (value: boolean, id: string) => void;
  handleToggleRti: (studentId: string, value: boolean) => void;
  handleMoveStudentToGroup: (student: ClassroomStudent | undefined) => void;
  handleDeleteStudentFromGroup: (student: ClassroomStudent | undefined) => void;
  t: TFunction<'translation', undefined, 'translation'>;
};

export const getStudentsColumns = ({
  availableLanguages,
  hasClassroomDigitalPracticeAccess,
  classroomCapabilities,
  classroomHasHomeAccess,
  practiceLevelOptions,
  isRti,
  isAnonymized,
  isRosteringEnabled,
  selectedInputKey,
  selectedStudentId,
  stage,
  weeklyTimeLimitOptions,
  isJourneys,
  handleBlurInput,
  handleDeleteStudent,
  handleInputChange,
  handleInviteParents,
  handleMoveStudent,
  handleOpenAvatarEditor,
  handleOpenChangePasswordModal,
  handleOpenCompleteManuallyModal,
  handleOpenDigitalPracticeAccessModal,
  handleOpenDigitalAccessConfirmationModal,
  handleResendInvitation,
  handleResetTests,
  handleSelect,
  handleToggleHomeAccess,
  handleToggleIndividualAccess,
  handleToggleRti,
  handleMoveStudentToGroup,
  handleDeleteStudentFromGroup,
  t,
}: Props): Column<Row>[] => {
  const columns: Column<Row>[] = [
    {
      id: 'firstName',
      label: t('students.table.labels.student-information'),
      width: '25%',
      minWidth: isAnonymized && stage !== Stage.SECUNDARIA ? '368px' : '268px',
      align: 'left',
      render: (_, row) =>
        renderStudentName({
          t,
          isRosteringEnabled,
          selectedInputKey,
          selectedStudentId,
          isAnonymized: isAnonymized && stage !== Stage.SECUNDARIA,
          handleBlurInput,
          handleInputChange,
          handleSelect,
          ...row,
        }),
      subLabel: () =>
        renderStudentName({
          t,
          isRosteringEnabled,
          isHeader: true,
          selectedInputKey,
          selectedStudentId,
          isAnonymized: isAnonymized && stage !== Stage.SECUNDARIA,
          handleBlurInput,
          handleInputChange,
          handleSelect,
        }),
    },
    {
      id: 'separator-1',
      width: 9,
      separator: true,
    },
  ];

  const homeAccessColumns: Column<Row>[] = [
    {
      id: 'separator-2',
      width: 9,
      separator: true,
    },
    {
      id: 'homeAccess',
      label: t('students.table.labels.digitalPracticeAccess'),
      width: '33%',
      minWidth: '332px',
      align: 'left',
      render: (_, row) =>
        renderHomeAccess({
          t,
          classroomHasHomeAccess,
          handleInputChange,
          handleInviteParents,
          handleToggleHomeAccess,
          hasClassroomDigitalPracticeAccess,
          weeklyTimeLimitOptions,
          ...row,
        }),
      subLabel: () =>
        renderHomeAccessSublabel({
          t,
          hasClassroomDigitalPracticeAccess,
          handleOpenDigitalAccessConfirmationModal,
          handleOpenDigitalPracticeAccessModal,
        }),
    },
  ];

  if (stage === Stage.INFANTIL) {
    columns.unshift({
      id: 'avatar',
      width: '64px',
      minWidth: '64px',
      align: 'center',
      render: (_, row) =>
        renderAvatar({
          handleOpenAvatarEditor,
          ...row,
        }),
    });
  }

  if (stage === Stage.INFANTIL) {
    columns.push({
      id: 'digitalPractice',
      label: t('students.table.labels.individualized-practice'),
      width: '20%',
      minWidth: '220px',
      align: 'left',
      render: (_, row) =>
        renderEyDigitalPractice({
          practiceLevelId: row?.bubblePlanId,
          practiceLevelOptions,
          handleInputChange,
          availableLanguages,
          selectedStudentId,
          selectedInputKey,
          handleSelect,
          handleBlurInput,
          weeklyTimeLimitOptions,
          ...row,
        }),
      subLabel: () => renderDigitalPracticeSubLabel({ t, stage }),
    });

    columns.push(...homeAccessColumns);
  }

  if (!isRti && stage === Stage.PRIMARIA) {
    columns.push(
      {
        id: 'digitalPractice',
        label: t('students.table.labels.individualized-practice'),
        width: '42%',
        minWidth: '428px',
        align: 'left',
        render: (_, row) =>
          renderPrimaryDigitalPractice({
            t,
            practiceLevelId: row?.bubblePlanId,
            practiceLevelOptions,
            handleInputChange,
            availableLanguages,
            selectedStudentId,
            selectedInputKey,
            handleSelect,
            handleBlurInput,
            handleToggleIndividualAccess,
            stage,
            isRosteringEnabled,
            classroomCapabilities,
            ...row,
          }),
        subLabel: () =>
          renderDigitalPracticeSubLabel({ t, stage, classroomCapabilities }),
      },
      ...homeAccessColumns
    );
  } else if (isRti && stage === Stage.PRIMARIA) {
    columns.push({
      id: 'intervention',
      label: t('students.table.labels.intervention'),
      width: '',
      minWidth: '428px',
      align: 'left',
      render: (_, row) =>
        renderIntervention({
          t,
          handleToggleRti,
          ...row,
        }),
      subLabel: () =>
        renderInterventionSubLabel({
          t,
        }),
    });
  }

  if (stage === Stage.SECONDARY || stage === Stage.SECUNDARIA) {
    columns.push({
      id: 'digitalPractice',
      label: t('students.table.labels.individualized-practice'),
      width: '60%',
      minWidth: '768px',
      align: 'left',
      render: (_, row) =>
        renderSecondaryDigitalPractice({
          t,
          practiceLevelId: row?.educationalPlanId,
          practiceLevelOptions,
          handleInputChange,
          availableLanguages,
          selectedStudentId,
          selectedInputKey,
          ...row,
        }),
      subLabel: () => renderDigitalPracticeSubLabel({ t, stage }),
    });
  }

  columns.push({
    id: 'actions',
    width: '100%',
    sticky: true,
    stickyPosition: 'right',
    render: (_, row) => {
      return (
        <ClassroomStudentsTableActionsCell
          student={row as ClassroomStudent}
          onMoveStudent={handleMoveStudent}
          onDeleteStudent={handleDeleteStudent}
          onChangePassword={handleOpenChangePasswordModal}
          onResetTests={handleResetTests}
          onResendInvitation={handleResendInvitation}
          onCompleteInvitation={handleOpenCompleteManuallyModal}
          onMoveStudentToGroup={handleMoveStudentToGroup}
          onDeleteStudentFromGroup={handleDeleteStudentFromGroup}
          stage={stage}
          isJourneys={isJourneys}
        />
      );
    },
  });

  return columns;
};
